/**
 * batch-66 verkoop-extractie (FO-493) - factuurregels koppelen aan dagregels
 * van werkbonnen. Puur: regels + kandidaat-dagregels in, de koppeling uit.
 *
 * De Finqle-factuur noemt per regel de medewerker en de datum (of de werkbon
 * met een periode), niet altijd een werkbonnummer. Via de dagregel van die
 * medewerker op die datum bij die klant komt de werkbon in beeld, en daarmee
 * het facturatieverzoek (FO-470).
 *
 * Bewust strikt: alleen een EENDUIDIGE treffer koppelt. Twee kandidaten zonder
 * onderscheid (tijden, PO-referentie) = meerduidig = niets koppelen.
 *
 * De PO-referentie beslist alleen als de regels zelf niets anders zeggen: elke
 * regel noemt niets (geen medewerker, geen werkbon) of wijst - met afwijkende
 * tijden - dezelfde werkbon aan als de PO. Noemt een regel een medewerker of
 * werkbon die NIET gevonden wordt, dan is dat tegenspraak en koppelt het systeem
 * niets: uren van de ene medewerker horen niet op de bon van een ander omdat
 * toevallig de PO klopt.
 *
 * "Noemt niets" moet bewezen zijn. Een regel waar niets aan te koppelen valt,
 * maar die wel iets noemt dat het systeem niet kon lezen (een datum of periode,
 * een werkbonnummer, een medewerker van deze klant, het begin van een bekende
 * regelsoort, of een uren-/dagtarief-/kostenregel zonder het deel waarop
 * gekoppeld wordt), houdt de PO-beslissing tegen. Alleen de PO-beslissing: zo'n
 * regel blijft "niets te koppelen" en staat het op gefactureerd zetten van een
 * werkbon die door de andere regels volledig gedekt is niet in de weg. En zijn de
 * regels van de factuur helemaal niet als losse regels bekend (`regelsOnbekend`),
 * dan ontbreekt het bewijs en beslist de PO nooit.
 */

import type {
  ExtractieRegelKoppelingDTO,
  ExtractieWerkbonDekkingDTO,
  ExtractieWerkbonKoppelingDTO,
  GestructureerdeFactuurRegel,
} from '@streep/types'
import { isoNaarNlDatum, naamSleutel, plet } from './tekst'

/** Een dagregel van een werkbon bij de klant, als kandidaat voor de koppeling. */
export interface DagregelKandidaat {
  id: string
  werkbonId: string
  werkbonNumber: string
  werkbonReferentie: string | null
  /** Naam van de medewerker op de dagregel (voornaam + achternaam); null = onbemand. */
  medewerkerNaam: string | null
  /** Kalenderdag van de dagregel (jjjj-mm-dd). */
  datum: string
  /** Werkelijke of geplande tijden (HH:MM). */
  start: string | null
  eind: string | null
  uren: number
  isAfwezig: boolean
}

export interface KoppelOptiesRegels {
  /** PO-nummer van de factuur (voor de werkbonreferentie en als scheidsrechter). */
  po: string | null
  /**
   * Werkbonnen (id) waarvan ALLE dagregels in `dagregels` zitten. Alleen van die
   * bonnen kan de dekking ("volledig gedekt") worden vastgesteld.
   */
  volledigGeladenWerkbonIds?: readonly string[]
  /**
   * Werkbonnen van deze klant waarvan de referentie gelijk is aan de PO, ook als
   * ze geen dagregels hebben (bv. alleen kosten). Aanvulling op wat uit de
   * dagregels zelf volgt.
   */
  poWerkbonnen?: readonly { werkbonId: string; werkbonNumber: string }[]
  /**
   * De regels van de factuur zijn niet als losse regels bekend (ze sluiten niet,
   * het teken is onzeker, of het is een scan). Zonder regelbewijs beslist de PO
   * nooit: wat de regels zeggen is dan onbekend, niet "niets".
   */
  regelsOnbekend?: boolean
}

/**
 * Een datum, periode of tijdvak in een GEPLETTE regelomschrijving. In geplette
 * tekst staat wat erop volgt er direct achter ("01-09-2026 07:00" wordt
 * "01-09-202607:00"): een patroon mag dus niet eisen dat er geen cijfer volgt.
 * Daarom een aannemelijke dag en maand, en een aannemelijk jaartal. Liever een
 * regel te veel als "noemt iets" dan een te weinig: dit houdt alleen de
 * PO-beslissing tegen. Alle herhalingen zijn begrensd.
 */
const NOEMT_DATUM_RES: readonly RegExp[] = [
  // 03-09-2026, 3/9/2026, 03.09.2026 en met een jaar van twee cijfers (dan niet
  // gevolgd door drie of meer cijfers: dat is een telefoon- of artikelnummer).
  /(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:(?:19|20)\d{2}|\d{2}(?!\d{3}))/,
  // 2026-09-03
  /(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])/,
  // "3 sept 2026", "1 september"
  /\d{1,2}(?:jan|feb|mrt|maart|apr|mei|jun|jul|aug|sep|okt|nov|dec)/i,
  // "week 36", "wk 36"
  /(?:week|wk)\d{1,2}/i,
  // een tijdvak: 17:00-19:00
  /\d{1,2}:\d{2}-\d{1,2}:\d{2}/,
]
/** Een werkbonnummer (zelfde vorm als de regel-parsers): letters gevolgd door 6 tot 9 cijfers. */
const NOEMT_WERKBON_RE = /[A-Za-z]{2,6}\d{6,9}/
const NOEMT_BEGIN_RE = /^(?:gewerkteuren|dagtarief|kilometervergoeding)/i
/** Een PO korter dan dit is te algemeen om uit een omschrijving weg te strepen. */
const MIN_PO_OM_WEG_TE_STREPEN = 6
/** Een naam korter dan dit (zonder spaties) is te algemeen om in een omschrijving te herkennen. */
const MIN_NAAM_IN_OMSCHRIJVING = 6

interface NoemtContext {
  /**
   * Verwijzingen die hetzelfde zeggen als de PO van de factuur (de PO zelf en de
   * nummers van de werkbonnen die die PO dragen), geplet en in hoofdletters: een
   * regel die alleen dat noemt spreekt de PO niet tegen.
   */
  zelfdeAlsPo: readonly string[]
  /** De medewerkers met een dagregel bij deze klant (naamSleutel). */
  medewerkerSleutels: readonly string[]
}

/**
 * Noemt een regel waar niets aan te koppelen viel toch iets? Dan is "de regel
 * noemt niets" niet bewezen en beslist de PO niet.
 *
 *   - een herkende regelsoort (uren, dagtarief, kosten) noemt per definitie werk
 *     van iemand op een dag; ontbreekt het deel waarop gekoppeld wordt, dan is
 *     dat niet "niets";
 *   - een niet-herkende regel die een medewerker, een datum, een ander
 *     werkbonnummer, een bekend begin of een medewerker van deze klant draagt.
 */
function noemtIetsOngelezen(regel: GestructureerdeFactuurRegel, ctx: NoemtContext): boolean {
  if (regel.soort !== 'OVERIG') return true
  if (regel.medewerker || regel.datum) return true
  if (regel.werkbonNummer && !ctx.zelfdeAlsPo.includes(plet(regel.werkbonNummer).toUpperCase())) return true
  let plat = plet(regel.omschrijving).slice(0, 600).toUpperCase()
  if (NOEMT_BEGIN_RE.test(plat)) return true
  const naam = naamSleutel(regel.omschrijving).slice(0, 600)
  if (ctx.medewerkerSleutels.some((m) => naam.includes(m))) return true
  for (const verwijzing of ctx.zelfdeAlsPo) {
    if (verwijzing.length >= MIN_PO_OM_WEG_TE_STREPEN) plat = plat.split(verwijzing).join(' ')
  }
  return NOEMT_WERKBON_RE.test(plat) || NOEMT_DATUM_RES.some((re) => re.test(plat))
}

function zelfdeNaam(regelNaam: string | null, dagregelNaam: string | null): boolean {
  const a = naamSleutel(regelNaam)
  const b = naamSleutel(dagregelNaam)
  return a.length >= 3 && a === b
}

function zelfdeWerkbon(a: string | null, b: string | null): boolean {
  if (!a || !b) return false
  return plet(a).toUpperCase() === plet(b).toUpperCase()
}

function zelfdeReferentie(po: string | null, referentie: string | null): boolean {
  if (!po || !referentie) return false
  const a = plet(po).toUpperCase()
  return a.length >= 3 && a === plet(referentie).toUpperCase()
}

function geen(regelIndex: number, toelichting: string): ExtractieRegelKoppelingDTO {
  return {
    regelIndex,
    status: 'GEEN',
    werkbonId: null,
    werkbonNumber: null,
    dagregelIds: [],
    zekerheid: null,
    toelichting,
  }
}

function nvt(regelIndex: number): ExtractieRegelKoppelingDTO {
  return {
    regelIndex,
    status: 'NVT',
    werkbonId: null,
    werkbonNumber: null,
    dagregelIds: [],
    zekerheid: null,
    toelichting: 'De regel noemt geen medewerker of werkbon.',
  }
}

/** Toelichting bij een regel waar niets aan te koppelen viel, maar die wel iets noemt. */
const ONGELEZEN_TOELICHTING =
  'De regel noemt een datum, medewerker of werkbon in een vorm die niet gelezen kon worden; er is niets aan gekoppeld.'

function koppelUren(
  regel: GestructureerdeFactuurRegel,
  index: number,
  dagregels: readonly DagregelKandidaat[],
  po: string | null,
): ExtractieRegelKoppelingDTO {
  if (!regel.medewerker || !regel.datum) return nvt(index)
  const opDag = dagregels.filter(
    (d) => !d.isAfwezig && d.datum === regel.datum && zelfdeNaam(regel.medewerker, d.medewerkerNaam),
  )
  const dag = isoNaarNlDatum(regel.datum)
  if (opDag.length === 0) {
    return geen(index, `Geen dagregel van ${regel.medewerker} op ${dag} bij deze klant.`)
  }
  const tijdenGelijk = (d: DagregelKandidaat) =>
    regel.startTijd !== null && regel.eindTijd !== null && d.start === regel.startTijd && d.eind === regel.eindTijd
  let kandidaten = opDag
  if (kandidaten.length > 1) {
    const opTijd = kandidaten.filter(tijdenGelijk)
    if (opTijd.length >= 1) kandidaten = opTijd
  }
  if (kandidaten.length > 1) {
    const opPo = kandidaten.filter((d) => zelfdeReferentie(po, d.werkbonReferentie))
    if (opPo.length >= 1) kandidaten = opPo
  }
  if (kandidaten.length > 1) {
    return {
      regelIndex: index,
      status: 'MEERDUIDIG',
      werkbonId: null,
      werkbonNumber: null,
      dagregelIds: kandidaten.map((d) => d.id),
      zekerheid: null,
      toelichting: `${regel.medewerker} heeft op ${dag} ${kandidaten.length} dagregels (${[...new Set(kandidaten.map((d) => d.werkbonNumber))].join(', ')}); koppel handmatig.`,
    }
  }
  const d = kandidaten[0]
  if (!d) return geen(index, `Geen dagregel van ${regel.medewerker} op ${dag} bij deze klant.`)
  const opTijd = tijdenGelijk(d)
  return {
    regelIndex: index,
    status: 'GEKOPPELD',
    werkbonId: d.werkbonId,
    werkbonNumber: d.werkbonNumber,
    dagregelIds: [d.id],
    zekerheid: opTijd ? 'high' : 'medium',
    toelichting: opTijd
      ? `Dagregel van ${regel.medewerker} op ${dag} (${d.werkbonNumber}).`
      : `Dagregel van ${regel.medewerker} op ${dag} (${d.werkbonNumber}); de tijden wijken af (factuur ${regel.startTijd ?? '?'}-${regel.eindTijd ?? '?'}, werkbon ${d.start ?? '?'}-${d.eind ?? '?'}).`,
  }
}

function koppelDagtarief(
  regel: GestructureerdeFactuurRegel,
  index: number,
  dagregels: readonly DagregelKandidaat[],
): ExtractieRegelKoppelingDTO {
  if (!regel.werkbonNummer || !regel.datum) return nvt(index)
  const vanBon = dagregels.filter((d) => zelfdeWerkbon(regel.werkbonNummer, d.werkbonNumber))
  if (vanBon.length === 0) return geen(index, `Werkbon ${regel.werkbonNummer} is niet gevonden bij deze klant.`)
  const tot = regel.datumTot ?? regel.datum
  const inPeriode = vanBon.filter(
    (d) =>
      !d.isAfwezig &&
      d.datum >= (regel.datum ?? '') &&
      d.datum <= tot &&
      (regel.medewerker === null || zelfdeNaam(regel.medewerker, d.medewerkerNaam)),
  )
  const eerste = vanBon[0]
  if (inPeriode.length === 0 || !eerste) {
    return geen(
      index,
      `Werkbon ${regel.werkbonNummer} heeft geen dagregels${regel.medewerker ? ` van ${regel.medewerker}` : ''} in de periode op de factuur.`,
    )
  }
  const dagen = regel.eenheid === 'dag' ? regel.eenheden : null
  const klopt = dagen === null || dagen === inPeriode.length
  return {
    regelIndex: index,
    status: 'GEKOPPELD',
    werkbonId: eerste.werkbonId,
    werkbonNumber: eerste.werkbonNumber,
    dagregelIds: inPeriode.map((d) => d.id),
    zekerheid: klopt ? 'high' : 'medium',
    toelichting: klopt
      ? `${inPeriode.length} dagregel${inPeriode.length === 1 ? '' : 's'} op ${eerste.werkbonNumber}.`
      : `${inPeriode.length} dagregel${inPeriode.length === 1 ? '' : 's'} gevonden op ${eerste.werkbonNumber}; de factuur rekent ${dagen} dag${dagen === 1 ? '' : 'en'}.`,
  }
}

function koppelKosten(
  regel: GestructureerdeFactuurRegel,
  index: number,
  dagregels: readonly DagregelKandidaat[],
  po: string | null,
): ExtractieRegelKoppelingDTO {
  if (!regel.werkbonNummer) {
    // Zonder werkbonnummer: als een urenregel behandelen (medewerker + datum).
    return regel.medewerker && regel.datum ? koppelUren(regel, index, dagregels, po) : nvt(index)
  }
  const vanBon = dagregels.filter((d) => zelfdeWerkbon(regel.werkbonNummer, d.werkbonNumber))
  const eerste = vanBon[0]
  if (!eerste) return geen(index, `Werkbon ${regel.werkbonNummer} is niet gevonden bij deze klant.`)
  const opDag = vanBon.filter(
    (d) =>
      !d.isAfwezig &&
      d.datum === regel.datum &&
      (regel.medewerker === null || zelfdeNaam(regel.medewerker, d.medewerkerNaam)),
  )
  return {
    regelIndex: index,
    status: 'GEKOPPELD',
    werkbonId: eerste.werkbonId,
    werkbonNumber: eerste.werkbonNumber,
    // Kosten horen bij de werkbon; een dagregel op de kostendatum is een bevestiging.
    dagregelIds: [],
    zekerheid: opDag.length > 0 ? 'high' : 'medium',
    toelichting:
      opDag.length > 0
        ? `Kosten op ${eerste.werkbonNumber}.`
        : `Kosten op ${eerste.werkbonNumber}; geen dagregel op de kostendatum gevonden.`,
  }
}

/**
 * Koppel de regels aan dagregels. `dagregels` zijn de kandidaten bij DEZE klant
 * (en BV) in de periode van de factuur.
 */
export function koppelRegelsAanDagregels(
  regels: readonly GestructureerdeFactuurRegel[],
  dagregels: readonly DagregelKandidaat[],
  opties: KoppelOptiesRegels,
): ExtractieWerkbonKoppelingDTO {
  const po = opties.po

  // ── PO-referentie: werkbonnen waarvan de referentie gelijk is aan de PO ──
  const poBonnen = new Map<string, string>()
  for (const d of dagregels) {
    if (zelfdeReferentie(po, d.werkbonReferentie)) poBonnen.set(d.werkbonId, d.werkbonNumber)
  }
  for (const w of opties.poWerkbonnen ?? []) poBonnen.set(w.werkbonId, w.werkbonNumber)
  // Wat hetzelfde zegt als de PO: de PO zelf en de nummers van die werkbonnen
  // (langste eerst, zodat een korter nummer een langer niet in stukken knipt).
  const noemtContext: NoemtContext = {
    zelfdeAlsPo: [po ?? '', ...poBonnen.values()]
      .map((v) => plet(v).toUpperCase())
      .filter((v) => v.length >= 3)
      .sort((a, b) => b.length - a.length),
    medewerkerSleutels: [...new Set(dagregels.map((d) => naamSleutel(d.medewerkerNaam)))].filter(
      (m) => m.length >= MIN_NAAM_IN_OMSCHRIJVING,
    ),
  }

  /**
   * Regels waar niets aan te koppelen viel, maar die wel iets noemen dat het
   * systeem niet kon lezen. Ze houden alleen de PO-beslissing tegen; voor de
   * dekking en het markeren tellen ze als "niets te koppelen".
   */
  const ongelezen = new Set<number>()
  const uitkomsten: ExtractieRegelKoppelingDTO[] = regels.map((regel, index) => {
    let uit: ExtractieRegelKoppelingDTO
    switch (regel.soort) {
      case 'UREN':
        uit = koppelUren(regel, index, dagregels, po)
        break
      case 'DAGTARIEF':
        uit = koppelDagtarief(regel, index, dagregels)
        break
      case 'KOSTEN':
        uit = koppelKosten(regel, index, dagregels, po)
        break
      default:
        uit = nvt(index)
    }
    if (uit.status === 'NVT' && noemtIetsOngelezen(regel, noemtContext)) {
      ongelezen.add(index)
      return { ...uit, toelichting: ONGELEZEN_TOELICHTING }
    }
    return uit
  })

  // ── Dekking per werkbon ──
  const volledigGeladen = new Set(opties.volledigGeladenWerkbonIds ?? [])
  const gedekt = new Map<string, Set<string>>()
  const viaRegels = new Map<string, string>()
  for (const u of uitkomsten) {
    if (u.status !== 'GEKOPPELD' || !u.werkbonId || !u.werkbonNumber) continue
    viaRegels.set(u.werkbonId, u.werkbonNumber)
    const set = gedekt.get(u.werkbonId) ?? new Set<string>()
    for (const id of u.dagregelIds) set.add(id)
    gedekt.set(u.werkbonId, set)
  }
  const werkbonnen: ExtractieWerkbonDekkingDTO[] = []
  for (const [werkbonId, werkbonNumber] of viaRegels) {
    const factureerbaar = dagregels.filter((d) => d.werkbonId === werkbonId && !d.isAfwezig && d.uren > 0)
    const set = gedekt.get(werkbonId) ?? new Set<string>()
    const gedekteDagregels = factureerbaar.filter((d) => set.has(d.id)).length
    werkbonnen.push({
      werkbonId,
      werkbonNumber,
      volledigGedekt:
        volledigGeladen.has(werkbonId) && factureerbaar.length > 0 && gedekteDagregels === factureerbaar.length,
      gedekteDagregels,
      totaalDagregels: factureerbaar.length,
      via: 'REGELS',
    })
  }

  // ── Werkbonnen die alleen via de PO-referentie in beeld komen ──
  for (const [werkbonId, werkbonNumber] of poBonnen) {
    if (viaRegels.has(werkbonId)) continue
    const factureerbaar = dagregels.filter((d) => d.werkbonId === werkbonId && !d.isAfwezig && d.uren > 0)
    werkbonnen.push({
      werkbonId,
      werkbonNumber,
      volledigGedekt: false,
      gedekteDagregels: 0,
      totaalDagregels: factureerbaar.length,
      via: 'PO',
    })
  }
  werkbonnen.sort((a, b) => a.werkbonNumber.localeCompare(b.werkbonNumber))

  const teKoppelen = uitkomsten.filter((u) => u.status !== 'NVT')
  const gekoppeld = teKoppelen.filter((u) => u.status === 'GEKOPPELD')
  const volledig = teKoppelen.length > 0 && gekoppeld.length === teKoppelen.length
  const viaRegelNummers = [...viaRegels.values()].sort()
  const poNummers = [...poBonnen.values()].sort()

  // ── De hele factuur op de PO-referentie ──
  // Alleen als de regels zelf niets zeker aanwijzen, precies EEN werkbon de PO
  // draagt en geen regel dat tegenspreekt. Tegenspraak = een regel die een
  // medewerker of werkbon noemt die niet gevonden is (GEEN), een meerduidige
  // regel, of een regel die - ook onzeker - naar een ANDERE bon wijst. Meerdere
  // bonnen op dezelfde PO (een doorlopende order) = niets: op een PO alleen
  // kiezen is gokken.
  const zekerGekoppeld = uitkomsten.some((u) => u.status === 'GEKOPPELD' && u.zekerheid === 'high')
  const enigePo = poBonnen.size === 1 ? [...poBonnen.entries()][0] : undefined
  // Een regel die iets ANDERS zegt dan de PO: niet gevonden, meerduidig, of naar een andere bon.
  const wijstAnders =
    enigePo !== undefined &&
    uitkomsten.some(
      (u) =>
        u.status === 'GEEN' ||
        u.status === 'MEERDUIDIG' ||
        (u.status === 'GEKOPPELD' && u.werkbonId !== enigePo[0]),
    )
  const tegenspraak = wijstAnders || ongelezen.size > 0
  const regelsOnbekend = opties.regelsOnbekend === true
  const poKoppeling =
    enigePo !== undefined && !zekerGekoppeld && !tegenspraak && !regelsOnbekend
      ? { werkbonId: enigePo[0], werkbonNumber: enigePo[1] }
      : null
  let poZin = ''
  if (poKoppeling) {
    poZin = ` De PO-referentie wijst werkbon ${poKoppeling.werkbonNumber} aan: de factuur wordt aan die werkbon gekoppeld.`
  } else if (enigePo !== undefined && regelsOnbekend) {
    poZin = ` De PO-referentie wijst werkbon ${enigePo[1]} aan. Zonder de regels koppelt het systeem daar niet automatisch op; koppel zo nodig handmatig vanuit het facturatieverzoek.`
  } else if (enigePo !== undefined && !zekerGekoppeld && wijstAnders) {
    poZin = ` De PO-referentie wijst werkbon ${enigePo[1]} aan, maar de regels wijzen niet naar die werkbon; er wordt niets automatisch gekoppeld.`
  } else if (enigePo !== undefined && !zekerGekoppeld) {
    poZin = ` De PO-referentie wijst werkbon ${enigePo[1]} aan, maar ${ongelezen.size === 1 ? 'een regel noemt' : `${ongelezen.size} regels noemen`} iets dat niet gelezen kon worden (een datum, medewerker of werkbon); er wordt niets automatisch op de PO gekoppeld.`
  } else if (poBonnen.size > 1 && !zekerGekoppeld) {
    // Bewust geen aantal: de aanroeper laadt een begrensde set PO-bonnen.
    poZin = ` De PO-referentie staat op meerdere werkbonnen (${poNummers.length > 3 ? `onder meer ${poNummers.slice(0, 3).join(', ')}` : poNummers.join(', ')}); daarop wordt niets automatisch gekoppeld.`
  }

  const onzeker = gekoppeld.filter((u) => u.zekerheid !== 'high').length
  let onzekerZin = ''
  if (onzeker > 0) {
    const regelWoord = onzeker === 1 ? 'regel' : 'regels'
    onzekerZin = ` Bij ${onzeker} ${regelWoord} wijkt de factuur af van de werkbon (tijden of aantal dagen)`
    // Via de PO krijgen ze het werkbonnummer alsnog; anders niet.
    onzekerZin += poKoppeling
      ? '.'
      : `; die ${onzeker === 1 ? 'regel krijgt' : 'regels krijgen'} geen werkbonnummer op de factuur.`
  }

  let samenvatting: string
  if (regelsOnbekend) {
    samenvatting = `De regels van de factuur zijn niet als losse regels uitgelezen; er wordt daarom niets per regel gekoppeld.${poZin}`
  } else if (teKoppelen.length === 0 && ongelezen.size > 0) {
    samenvatting = `Aan de regels viel niets te koppelen: ${ongelezen.size === 1 ? 'een regel noemt' : `${ongelezen.size} regels noemen`} iets dat niet gelezen kon worden (een datum, medewerker of werkbon).${poZin}`
  } else if (teKoppelen.length === 0) {
    samenvatting = `De regels noemen geen medewerker of werkbon${poZin === '' ? '; er is niets te koppelen.' : `.${poZin}`}`
  } else if (volledig) {
    samenvatting = `Alle ${teKoppelen.length} regels zijn gekoppeld aan ${viaRegelNummers.length === 1 ? 'werkbon' : 'werkbonnen'} ${viaRegelNummers.join(', ')}.${onzekerZin}${poZin}`
  } else if (gekoppeld.length > 0) {
    samenvatting = `${gekoppeld.length} van de ${teKoppelen.length} regels zijn gekoppeld (${viaRegelNummers.join(', ')}); de rest vraagt een handmatige controle.${onzekerZin}${poZin}`
  } else {
    samenvatting = `Geen van de regels kon aan een dagregel worden gekoppeld.${poZin}`
  }

  return {
    regels: uitkomsten,
    werkbonnen,
    poWerkbonNummers: poNummers,
    poKoppeling,
    volledig,
    samenvatting,
  }
}
